import React, { useEffect, useState } from 'react';
import { FileText, Upload, Loader2, FileIcon, Download, Trash2, BookOpen, ExternalLink } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const ResourcesPanel = ({ roomId, token, role }) => {
  const [items, setItems] = useState([]);
  const [sharedItems, setSharedItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = async () => {
    try {
      const r = await classroomApi.resourcesList(roomId, token);
      setItems(r.items || []);
    } catch (_e) {}
    try {
      const s = await classroomApi.sharedResourcesList(roomId, token);
      setSharedItems(s.items || []);
    } catch (_e) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roomId, token]);

  const cloudinaryUpload = async (file, sig) => {
    // Direct browser → Cloudinary upload (API secret stays on the server)
    const fd = new FormData();
    fd.append('file', file);
    fd.append('api_key', sig.api_key);
    fd.append('timestamp', sig.timestamp);
    fd.append('signature', sig.signature);
    fd.append('folder', sig.folder);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', sig.upload_url);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { reject(new Error('Bad response from upload')); }
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(fd);
    });
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (role !== 'tutor') { toast.error('Only the tutor can upload resources'); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error('Max 200 MB per file'); return; }

    setUploading(true); setProgress(0);
    try {
      // 1) Request signed upload params from our backend
      const sig = await classroomApi.resourceSign(roomId, token);
      // 2) Upload file directly to Cloudinary
      const result = await cloudinaryUpload(f, sig);
      // 3) Confirm with backend → persist metadata
      const saved = await classroomApi.resourceConfirm(roomId, token, {
        filename: f.name,
        mime_type: f.type || 'application/octet-stream',
        size_bytes: f.size,
        public_id: result.public_id,
        secure_url: result.secure_url,
        resource_type: result.resource_type || 'raw',
      });
      setItems((prev) => [saved, ...prev]);
      toast.success('Uploaded to class drive');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const remove = async (resourceId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await classroomApi.resourceDelete(roomId, token, resourceId);
      setItems((prev) => prev.filter((x) => x.resource_id !== resourceId));
      toast.success('Deleted');
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="classroom-resources">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-red-700" />
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Class drive</div>
        </div>
        {role === 'tutor' && (
          <label className="cursor-pointer text-[11px] uppercase tracking-wider font-bold text-red-700 hover:text-red-800 flex items-center gap-1.5"
                 data-testid="classroom-resources-upload">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? `Uploading ${progress}%` : 'Upload'}
            <input type="file" className="hidden" onChange={onPick} disabled={uploading}
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.webp" />
          </label>
        )}
      </div>
      {uploading && (
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-red-600 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
        {sharedItems.length > 0 && (
          <div className="bg-red-50/60 border-b border-red-100" data-testid="classroom-shared-resources">
            <div className="px-4 py-1.5 text-[9px] uppercase tracking-wider text-red-700 font-black flex items-center gap-1.5">
              <BookOpen className="h-3 w-3" /> Shared from Learning Centre
            </div>
            {sharedItems.map((s) => (
              <div key={s.share_id} className="px-4 py-2 flex items-center gap-3"
                data-testid={`shared-resource-${s.share_id}`}>
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-4 w-4 text-red-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{s.resource_title}</div>
                  <div className="text-[10px] text-gray-500">
                    {s.resource_kind} · {s.track_slug || 'no track'} · added by {s.added_by}
                  </div>
                </div>
                <a href={`/student/learning?topic=${encodeURIComponent(s.resource_slug)}`}
                  target="_blank" rel="noreferrer"
                  className="h-8 px-3 rounded-full bg-red-700 hover:bg-red-800 text-white text-[10px] font-black uppercase tracking-wider flex items-center gap-1"
                  data-testid={`shared-resource-open-${s.share_id}`}>
                  <ExternalLink className="h-3 w-3" /> Open
                </a>
              </div>
            ))}
          </div>
        )}
        {items.length === 0 && sharedItems.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400">No materials shared yet.</div>
        )}
        {items.map((it) => (
          <div key={it.resource_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`resource-${it.resource_id}`}>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <FileIcon className="h-4 w-4 text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{it.filename}</div>
              <div className="text-[11px] text-gray-500">
                {fmtBytes(it.size_bytes)} · {it.uploaded_by_role} · {new Date(it.uploaded_at).toLocaleString()}
              </div>
            </div>
            {it.storage_url ? (
              <a href={it.storage_url} target="_blank" rel="noreferrer" download={it.filename}
                 className="h-8 px-3 rounded-full bg-black text-white text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-gray-900">
                <Download className="h-3 w-3" /> Open
              </a>
            ) : (
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Pending</span>
            )}
            {role === 'tutor' && (
              <button onClick={() => remove(it.resource_id)}
                className="h-8 w-8 rounded-full border border-gray-300 text-red-700 hover:bg-red-50 flex items-center justify-center"
                data-testid={`resource-delete-${it.resource_id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResourcesPanel;
